<script setup generic="T" lang="ts">
import type { ComponentPublicInstance } from 'vue';
import {
  addDays,
  addMonths,
  addYears,
  endOfDay,
  isAfter,
  isBefore,
  startOfDay,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';
import {
  computed,
  nextTick,
  ref,
  toRef,
  watch,
} from 'vue';

import type { DateSingleInputType } from '@modules/forms/types';
import useDateInput from '@modules/forms/components/inputs/composables/useDateInput';
import VFormFloatLabelInputWrapper
  from '@modules/forms/components/inputs/VFormFloatLabelInputWrapper/VFormFloatLabelInputWrapper.vue';
import VFormInputWrapper
  from '@modules/forms/components/inputs/VFormInputWrapper/VFormInputWrapper.vue';
import { FORM_DATE_INPUT_PT_BORDER_FIX } from '@modules/forms/constants';
import { isDate } from '@utils/guards';

import type {
  VFormDateRangeInputEmits,
  VFormDateRangeInputProps,
} from './types';
import { useInputInnerValue } from '../composables';

const props = withDefaults(defineProps<VFormDateRangeInputProps>(), {});
const emit = defineEmits<VFormDateRangeInputEmits>();

interface DatePickerLike extends ComponentPublicInstance {
  rawValue?: DateSingleInputType;
  updateCurrentMetaData?(): void;
}

const { innerValue, setValue } = useInputInnerValue(toRef(props, 'value'));
const {
  format,
  isMobile,
  inputPtClass,
  adjustDateForViewMode,
  monthPtFunction,
  yearPtFunction,
} = useDateInput(toRef(props, 'dateFormat'));

const startCalendarRef = ref<DatePickerLike | null>(null);
const endCalendarRef = ref<DatePickerLike | null>(null);

const startInputId = computed(() => props.name ? `${props.name}-start` : undefined);
const endInputId = computed(() => props.name ? `${props.name}-end` : undefined);

function syncCalendarView(calendar: DatePickerLike | null, value?: DateSingleInputType): void {
  if (!calendar)
    return;

  // Work around PrimeVue calendar UI desync when v-model is changed programmatically.
  calendar.rawValue = value;
  calendar.updateCurrentMetaData?.();
}

function getSecondDateByRange(date: Date, dateToCompare: Date, forward: boolean): Date {
  if (!props.maxRange)
    return dateToCompare;
  const rangeValue = props.maxRange.value;
  let boundaryDate: Date;
  switch (props.maxRange.unit) {
    case 'day':
      boundaryDate = forward ? addDays(date, rangeValue) : subDays(date, rangeValue);
      break;
    case 'month':
      boundaryDate = forward ? addMonths(date, rangeValue) : subMonths(date, rangeValue);
      break;
    case 'year':
      boundaryDate = forward ? addYears(date, rangeValue) : subYears(date, rangeValue);
      break;
    default:
      return dateToCompare;
  }

  if (forward) {
    return isAfter(dateToCompare, boundaryDate) ? boundaryDate : dateToCompare;
  }

  return isBefore(dateToCompare, boundaryDate) ? boundaryDate : dateToCompare;
}

const startDate = computed<DateSingleInputType>({
  get: () => innerValue.value?.[0],
  set: (value) => {
    let newValue = value;
    let endDateBounded = innerValue.value?.[1];

    if (isDate(newValue)) {
      newValue = adjustDateForViewMode(props.view, newValue);

      if (!props.showTime) {
        newValue = startOfDay(newValue);
      }

      if (props.maxRange && isDate(endDateBounded)) {
        endDateBounded = getSecondDateByRange(newValue, endDateBounded, true);
        endDateBounded = adjustDateForViewMode(props.view, endDateBounded);
      }

      if (isDate(endDateBounded) && isAfter(newValue, endDateBounded)) {
        endDateBounded = newValue;
      }
    }

    setValue([newValue, endDateBounded]);
    return newValue;
  },
});

const endDate = computed<DateSingleInputType>({
  get: () => innerValue.value?.[1],
  set: (value) => {
    let newValue = value;
    let startDateBounded = innerValue.value?.[0];

    if (isDate(newValue)) {
      newValue = adjustDateForViewMode(props.view, newValue);

      if (!props.showTime) {
        newValue = endOfDay(newValue);
      }

      if (props.maxRange && isDate(startDateBounded)) {
        startDateBounded = getSecondDateByRange(newValue, startDateBounded, false);
        startDateBounded = adjustDateForViewMode(props.view, startDateBounded);
      }

      if (isDate(startDateBounded) && isBefore(newValue, startDateBounded)) {
        startDateBounded = newValue;
      }
    }

    setValue([startDateBounded, newValue]);
    return newValue;
  },
});

watch(format, async () => {
  const val = innerValue.value;
  setValue(undefined);
  await nextTick();
  setValue(val);
});

watch(() => props.value, async (value) => {
  await nextTick();
  // Keep both calendar panels aligned with externally updated model values.
  syncCalendarView(startCalendarRef.value, value?.[0]);
  syncCalendarView(endCalendarRef.value, value?.[1]);
});
</script>

<template>
  <VFormInputWrapper v-data-qa-auto-id="'VFormDateRangeInput'"
    :errors="errors"
    :class="isFullWidth ? 'w-full' : 'input-max-width'"
    class="flex"
    :hide-errors="hideErrors"
    :readonly="readonly"
  >
    <VFormFloatLabelInputWrapper
      :name="startInputId"
      :label="label"
      :icon-left="iconLeft"
      :required="required"
      :readonly="readonly"
    >
      <PInputCalendar
        ref="startCalendarRef"
        v-model="startDate"
        :name="startInputId"
        :input-id="startInputId"
        :placeholder="placeholder"
        :required="required"
        :readonly="readonly"
        :disabled="disabled"
        :date-format="format"
        :touch-u-i="isMobile"
        :manual-input="false"
        :view="view"
        :show-time="showTime"
        :min-date="minDate"
        :max-date="maxDate"
        show-icon
        icon-display="input"
        hide-on-date-time-select
        hide-on-range-selection
        class="w-full"
        :class="{ 'p-invalid': isInvalid && !readonly }"
        :pt="{
          pcInputText: {
            root: {
              class: `${inputPtClass} rounded-e-none`,
              autocomplete: autocomplete ? autocomplete : 'off',
            },
          },
          month: monthPtFunction,
          year: yearPtFunction,
          ...FORM_DATE_INPUT_PT_BORDER_FIX,
        }"
        @close="emit('blur', innerValue)"
        @focus="emit('focus', innerValue)"
        @update:model-value="emit('change', innerValue)"
        @input="emit('input', innerValue)"
      />
    </VFormFloatLabelInputWrapper>
    <VFormFloatLabelInputWrapper
      :name="endInputId"
      :label="labelEndDate"
      :icon-right="iconRight"
      :required="required"
      :readonly="readonly"
    >
      <PInputCalendar
        ref="endCalendarRef"
        v-model="endDate"
        :name="endInputId"
        :input-id="endInputId"
        :placeholder="placeholder"
        :required="required"
        :readonly="readonly"
        :disabled="disabled"
        :date-format="format"
        :touch-u-i="isMobile"
        :manual-input="false"
        :view="view"
        :show-time="showTime"
        :min-date="minDate"
        :max-date="maxDate"
        show-icon
        icon-display="input"
        hide-on-date-time-select
        hide-on-range-selection
        class="w-full"
        :class="{ 'p-invalid': isInvalid && !readonly }"
        :pt="{
          pcInputText: {
            root: {
              class: `${inputPtClass} rounded-s-none`,
              autocomplete: autocomplete ? autocomplete : 'off',
            },
          },
          month: monthPtFunction,
          year: yearPtFunction,
          ...FORM_DATE_INPUT_PT_BORDER_FIX,
        }"
        @close="emit('blur', innerValue)"
        @focus="emit('focus', innerValue)"
        @update:model-value="emit('change', innerValue)"
        @input="emit('input', innerValue)"
      />
    </VFormFloatLabelInputWrapper>
  </VFormInputWrapper>
</template>
